/**
 * The searchlights of a night battery, as *things that can be shot* — AD-017
 * §11.
 *
 * AD-017 shipped the beams as a renderer effect driven off `FlakSlot` state:
 * pretty, and free, and unanswerable. A light that sweeps you and cannot be
 * touched is scenery. This module is the other half — every beam now leaves a
 * real entity standing on real ground, with integrity, and putting one out is
 * a thing the guns behind it notice.
 *
 * ## Why they are `EncounterTarget`s and not a third list
 *
 * Because they are exactly the shape `targets.ts` already describes: a pose, an
 * integrity value, and a fire. Riding that list rather than inventing a
 * `searchlights: readonly Searchlight[]` beside `flak` buys the whole feature
 * off machinery that is already tested — `gun-pass.ts` puts every opposing
 * live target into every shooter's collision list, so they are strafeable with
 * no new pass; `bombs.ts` blasts them; `encounter-hash.ts` hashes them, so a
 * shot-out lamp is part of the fingerprint without a line of serialization
 * being written. Two profile facts keep them from *behaving* like objectives:
 * `strategic: false` (nothing is decided by them) and `bakedModelId: null`
 * (they are not drawn from the baked corpus).
 *
 * A battery itself stays what `flak.ts` says it is — a thing that shoots and is
 * not shot at, because `aa.c` gives it no health and no `.WLD` placement puts a
 * model at one. The *lights* are the port's own addition and the port's own
 * addition is what carries the health.
 *
 * ## Where they stand
 *
 * Two per battery, matching the two beams AD-017 draws, at a fixed standoff
 * **purely along the front's own axis** — `±SEARCHLIGHT_STANDOFF_M` in world
 * `x`, never in `z`.
 *
 * That is a deliberate constraint rather than a shortcut. `flak-placement.ts`
 * sets a battery back `FLAK_SETBACK_M` behind its own wire and the mission
 * suite asserts that every target is on its own side of the line
 * (`mission-generators.test.ts`); an offset with a `z` component would be a
 * standing invitation to put a German light in French mud the first time a
 * generator placed a battery near the crossing. Spreading them along the line
 * is also what a real position did — the lights sat clear of the guns so a
 * muzzle flash did not blind the crew, and clear of each other so one bomb
 * could not have both.
 *
 * ## Determinism
 *
 * Every position here is a pure function of the battery's own emplacement, and
 * every id a pure function of its id. Nothing is drawn from an RNG stream, so
 * placing the lights cannot shift what any other draw in the mission produces —
 * AD-007a's rule, applied at setup time the way `flak-placement.ts` applies it.
 */

import type { Vec3 } from "../flight/math3d.ts";
import type { FlakBatterySpec } from "./flak.ts";
import type { EncounterTargetSpec, EncounterTarget } from "./targets.ts";
import { targetProfileOf } from "./target-profiles.ts";

/** The kind the posts are created as. Named once, so nobody spells it twice. */
export const SEARCHLIGHT_KIND = "searchlight" as const;

/**
 * Lights per emplacement. Two, and it must stay two: AD-017's
 * `BEAMS_PER_BATTERY` is the same number seen from the renderer, and the beam a
 * post owns is found by its own index.
 */
export const SEARCHLIGHTS_PER_BATTERY = 2;

/**
 * Metres either side of the guns, along the front. Far enough that a strafing
 * run lined up on one light is not lined up on the other — at 45 m/s a pilot
 * crosses 120 m in under three seconds — and close enough that both are inside
 * the same battery's own patch of ground.
 */
export const SEARCHLIGHT_STANDOFF_M = 60;

/**
 * How a post's id is built from its battery's. The separator is `~` because no
 * generator's own ids use one, so `searchlightBatteryId` can undo this without
 * a table: `flak-h-1` gives `flak-h-1~light0` and `flak-h-1~light1`.
 */
export function searchlightPostId(batteryId: string, index: number): string {
  return `${batteryId}~light${index}`;
}

const POST_ID_PATTERN = /^(.+)~light(\d+)$/;

/** The battery a post belongs to, or `null` for an id that is not a post's. */
export function searchlightBatteryId(postId: string): string | null {
  return POST_ID_PATTERN.exec(postId)?.[1] ?? null;
}

/** Which of its battery's lights this is, or `null` for an id that is not a post's. */
export function searchlightPostIndex(postId: string): number | null {
  const index = POST_ID_PATTERN.exec(postId)?.[2];
  return index === undefined ? null : Number.parseInt(index, 10);
}

/** Where this battery's `index`-th light stands. Pure in the emplacement — see the module note on the `x`-only offset. */
export function searchlightPostPosition(battery: FlakBatterySpec, index: number): Vec3 {
  // Index 0 to the west, index 1 to the east; a third would sit on the guns,
  // which is why `SEARCHLIGHTS_PER_BATTERY` is not a free parameter.
  const offset = (index * 2 - (SEARCHLIGHTS_PER_BATTERY - 1)) * SEARCHLIGHT_STANDOFF_M;
  return {
    x: battery.position.x + offset,
    y: battery.position.y + targetProfileOf(SEARCHLIGHT_KIND).centreHeightM,
    z: battery.position.z,
  };
}

/** The pair of posts one battery is served by, in index order. */
export function searchlightPostsFor(battery: FlakBatterySpec): EncounterTargetSpec[] {
  return Array.from({ length: SEARCHLIGHTS_PER_BATTERY }, (_unused, index) => ({
    id: searchlightPostId(battery.id, index),
    kind: SEARCHLIGHT_KIND,
    side: battery.side,
    position: searchlightPostPosition(battery, index),
    label: `${targetProfileOf(SEARCHLIGHT_KIND).displayName} ${battery.id}/${index + 1}`,
  }));
}

/** Every battery's posts, in battery order — what a night mission adds to its target list. */
export function searchlightPosts(batteries: readonly FlakBatterySpec[]): EncounterTargetSpec[] {
  return batteries.flatMap(searchlightPostsFor);
}

/**
 * A post that is burning or gone is a post that is not throwing light. Burning
 * counts because the lamp is out the moment the arc house is holed; the half
 * second of fire that follows is the wreck settling, not the light working.
 */
export function searchlightLit(post: EncounterTarget): boolean {
  return !post.destroyed && !post.burning;
}

/**
 * The batteries whose lights are **all** out — the set `flak.ts` takes to mean
 * "these guns are firing on sound alone" (AD-010 §10).
 *
 * A battery with no posts at all is **not** in the set, and that is the whole
 * safety property of this function: every encounter that predates AD-017 §11,
 * and every daylight mission, places no posts and therefore blinds nobody, so
 * `stepFlak` behaves exactly as it always has. Only a battery that was *given*
 * lights can lose them.
 *
 * Returns `undefined` rather than an empty set when nothing is blind, so the
 * common case hands `stepFlak` nothing to look at.
 */
export function blindBatteryIds(targets: readonly EncounterTarget[]): ReadonlySet<string> | undefined {
  let lit: Set<string> | undefined;
  let dark: Set<string> | undefined;
  for (const post of targets) {
    if (post.kind !== SEARCHLIGHT_KIND) continue;
    const batteryId = searchlightBatteryId(post.id);
    if (batteryId === null) continue;
    if (searchlightLit(post)) (lit ??= new Set()).add(batteryId);
    else (dark ??= new Set()).add(batteryId);
  }
  if (dark === undefined) return undefined;
  for (const batteryId of lit ?? []) dark.delete(batteryId);
  return dark.size === 0 ? undefined : dark;
}
